import React from "react";
import DialogComponent, {DIALOG_TYPE} from "Core/components/DialogComponent";
import PropTypes from "prop-types";
import Label from "Core/components/display/Label";
import {MESSAGE_STYLE} from "Core/components/global/Message/const";
import {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from "Core/components/display/Button";
import {getBoolean, getString, isset} from "Core/helpers/data";

/**
 * Simple text dialog component
 */
class TextDialog extends DialogComponent {
	constructor(props) {
		super(props, {
			translationPath: 'TextDialog',
			alignContent: getString(props, 'alignContent', TextDialog.defaultProps.alignContent, true),
			dialogType: DIALOG_TYPE.MESSAGE,
		});
	}

	/**
	 * Render message dialog action buttons
	 * @note Message dialog has one close style button that closes the dialog on click.
	 *
	 * @param {string|null} [closeLabel] - Label used for the close button. Default value will be loaded from translation
	 * file.
	 * @param {string|null} [closeIcon] - Optional icon used for the close button.
	 * @param {string} [buttonStyle] - Button style (see BUTTON_STYLE const).
	 * @param {string} [buttonType] - Button display type (see BUTTON_DISPLAY_TYPE const).
	 * @return {*} Action buttons JSX to use in the main render method.
	 */
	renderMessageActionButtons(
		closeLabel = undefined, closeIcon = undefined, buttonStyle = BUTTON_STYLE.SUBTLE,
		buttonType = BUTTON_DISPLAY_TYPE.TRANSPARENT
	) {
		return this.renderActionButtons([
			{
				style: buttonStyle,
				type: buttonType,
				label: (isset(closeLabel) && closeLabel !== null ? closeLabel : this.t('Close', 'general')),
				icon: (closeIcon ? closeIcon : ''),
				onClick: this.close,
			}
		]);
	}

	render() {
		const {content, style, className, buttonOptions, titleLabel} = this.props;

		// Do not render the dialog if message object is empty or not defined
		if (!content) return null;
		
		return this.renderDialog((
			style === MESSAGE_STYLE.HELP ? this.renderHelpTitle(titleLabel) :
			style === MESSAGE_STYLE.INFO ? this.renderInfoTitle(titleLabel) :
			style === MESSAGE_STYLE.ERROR ? this.renderErrorTitle(titleLabel) :
			style === MESSAGE_STYLE.SUCCESS ? this.renderSuccessTitle(titleLabel) :
			style === MESSAGE_STYLE.WARNING ? this.renderWarningTitle(titleLabel) : 
			null
		), (
			<div className={`message-dialog-component ${className}`}>
				{
					typeof content === 'string' ?
						<Label content={content} element="p" supportHtml={getBoolean(this.props, 'supportHtml')} /> 
						:
						content
				}
			</div>
		), ...buttonOptions)
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
TextDialog.propTypes = {
	// Unique GUI ID of the dialog
	// @note This is automatically sent by the global Dialog component.
	dialogGUIID: PropTypes.string,
	// Dialog options
	// @note This is automatically sent by the global Dialog component.
	dialogOptions: PropTypes.object,
	// Action used to close the dialog
	// @note This is automatically sent by the global Dialog component.
	dialogCloseAction: PropTypes.func,

	// Text message 
	content: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	// Set to true to support HTML in 'message' prop.
	// @warning Be careful when using this flag because it can cause security issues. It uses 'dangerouslySetInnerHTML' 
	// to allow HTML content.
	supportHtml: PropTypes.bool,
	// Dialog style
	// @see 'MESSAGE_STYLE' const for available styles.
	style: PropTypes.string,
	// Dialog component additional class name
	className: PropTypes.string,
	// Use this to override dialogs default button options
	// @note See 'renderMessageActionButtons' method for reference
	buttonOptions: PropTypes.array,
	// Use this to override dialogs title label
	// @note This label should already be translated.
	titleLabel: PropTypes.string,
	// CSS text-align value that will be used for dialog content
	// @type {'left'|'right'|'center'|'justify'|'initial'|'inherit'}
	alignContent: PropTypes.oneOf(['left', 'right', 'center', 'justify', 'initial', 'inherit']),
	
	// Events
	onClose: PropTypes.func
};

/**
 * Define component default values for own props
 */
TextDialog.defaultProps = {
	content: '',
	supportHtml: false,
	style: MESSAGE_STYLE.DEFAULT,
	className: '',
	buttonOptions: [],
	titleLabel: '',
	alignContent: 'center',
};

export default TextDialog;